import React from 'react'
import { Button, Empty, Notification } from '@douyinfe/semi-ui'
import { IconRefresh, IconAlertTriangle } from '@douyinfe/semi-icons'
import { reportFromBoundary } from '@src/utils/errorReporter'
import { useIntl } from 'react-intl'

interface Props {
	children: React.ReactNode
	/** 错误时显示在通知里的标题后缀，便于定位是哪个区域出错 */
	scope?: string
}

interface State {
	hasError: boolean
	error: Error | null
}

/**
 * 全局错误边界
 * - 包裹任意子组件树，捕获 render 阶段的异常
 * - 错误时：
 *   1) 调 errorReporter 上报（已 stub，后端就绪后开箱即用）
 *   2) 弹 Notification 提示用户
 *   3) 渲染降级 UI（Empty + 重试按钮），Layout / Sider / Header 不受影响
 * - 重试：调用 onReset 清掉 hasError，子树重新挂载
 */
export class ErrorBoundary extends React.Component<Props, State> {
	state: State = { hasError: false, error: null }

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error }
	}

	componentDidCatch(error: Error, info: React.ErrorInfo): void {
		// 1) 上报后端
		reportFromBoundary(error, { componentStack: info.componentStack })
		// 2) 弹通知 —— 用 setTimeout 0 避免在 render 阶段调副作用
		setTimeout(() => {
			Notification.error({
				title: '页面出错了',
				content: error?.message || '未知错误，请稍后重试或联系管理员',
				duration: 6,
				icon: <IconAlertTriangle />
			})
		}, 0)
	}

	handleReset = (): void => {
		this.setState({ hasError: false, error: null })
	}

	render(): React.ReactNode {
		if (this.state.hasError) {
			return <ErrorFallback message={this.state.error?.message} onReset={this.handleReset} scope={this.props.scope} />
		}
		return this.props.children
	}
}

interface FallbackProps {
	message?: string
	onReset: () => void
	scope?: string
}

const ErrorFallback: React.FC<FallbackProps> = ({ message, onReset, scope }) => {
	const { formatMessage: i18nMsg } = useIntl()
	return (
		<div
			style={{
				padding: '64px 24px',
				minHeight: 240,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center'
			}}
		>
			<Empty
				image={<IconAlertTriangle size="extra-large" style={{ color: 'var(--semi-color-warning)' }} />}
				title={scope ? `${scope} 加载失败` : '页面加载失败'}
				description={message || '该区域发生异常，已自动上报。你可以点击重试或返回首页。'}
			/>
			<div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
				<Button icon={<IconRefresh />} type="primary" theme="solid" onClick={onReset}>
					{i18nMsg({ id: 'app.system.common.button.refresh' })}
				</Button>
			</div>
		</div>
	)
}

export default ErrorBoundary
